import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAssignTag,
  useEditResource,
  useResourceRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import {
  Assignable,
  Identifiable,
  planzone,
  project,
  Removable,
  Resource,
} from "@planzone/dto";
import { Tag } from "@common/tag";
import { SelectList } from "@common/selectList";
import { AddTagIcon } from "@scenes/resourceEdition/form/addTagIcon";
import { TagForm } from "@common/tagForm";
import { Checkbox } from "@common/checkbox";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Safeguard } from "@scenes/safeguard";
import { ResourceSecondaryCriteria } from "@scenes/resourceSecondaryCriteria";
import { Form } from "./form";
import { InfoResource } from "./infoResource";
import { AssignCostProfileToResource } from "./assignCostProfileToResource";
import { AssignCalendarHolidaysToResource } from "./assignCalendarHolidaysToResource";
import { Calendar } from "./calendar";
import { Projects } from "./projects";
import { AllocationManagement } from "./allocationManagement";
import { Props } from "./types";
import styles from "./styles.scss";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";

function ResourceEdition({ id, cancel }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone: currentPlanzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    currentPlanzone.uuid,
    "resource",
  );
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    resource,
    retrieveResource,
  } = useResourceRetrieval(id);
  const { isRequestPending: isAssignTagRequestPending, assignTag } =
    useAssignTag();
  const { isRequestPending: isEditionRequestPending, editResource } =
    useEditResource(resource);
  const [formData, setFormData] =
    React.useState<Identifiable<Resource>>(resource);

  React.useEffect(() => {
    setFormData(resource);
  }, [resource]);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveResource(id);
  }, [id]);

  return (
    <Modal header={t("editResource.title")} close={cancel}>
      <div className={styles.resourceEdition}>
        <div className={styles.body}>
          <div className={styles.col}>
            <div className={styles.header}>
              <InfoResource resource={resource} />
              <AssignCostProfileToResource resource={resource} />
            </div>
            <Form
              formData={formData}
              editForm={setFormData}
              submitForm={(formData) =>
                !isRequestPending &&
                editResource({ resource, nextResource: formData })
              }
            />
            <Safeguard
              useCases={[UseCases.editResourceDetailsConfiguration]}
              context={{}}
            >
              {({ canEditResourceDetailsConfiguration }) =>
                (template.length > 0 ||
                  canEditResourceDetailsConfiguration) && (
                  <div className={styles.secondaryCriteria}>
                    <div className={styles.secondaryCriteriaHeader}>
                      <p className={styles.label}>
                        {t("editResource.secondaryCriteria")}
                      </p>
                      {canEditResourceDetailsConfiguration ? (
                        <div
                          className={styles.configurationIconBox}
                          onClick={() =>
                            pushView({
                              key: UseCases.retrieveResourceDetailsConfiguration,
                              data: undefined,
                            })
                          }
                        >
                          <CogWheelIcon className={styles.configurationIcon} />
                        </div>
                      ) : (
                        <div className={styles.contextualMenuIcon} />
                      )}
                    </div>
                    <ResourceSecondaryCriteria
                      criteriaForms={template.reduce(
                        (acc, criteria) => ({
                          ...acc,
                          [criteria.uuid]: (
                            <SecondaryCriteriaForm
                              criteria={criteria}
                              value={
                                resource.attributes
                                  ? resource.attributes[criteria.uuid] || ""
                                  : ""
                              }
                              flexible
                              disabled={isEditionRequestPending}
                              submit={(value) =>
                                editResource({
                                  resource,
                                  nextResource: {
                                    ...resource,
                                    attributes: {
                                      ...resource.attributes,
                                      [criteria.uuid]: value,
                                    },
                                  },
                                })
                              }
                            />
                          ),
                        }),
                        {},
                      )}
                    />
                  </div>
                )
              }
            </Safeguard>
          </div>
          <div className={styles.col}>
            <div className={styles.tags}>
              <h2 className={styles.title}>{t("editResource.tagsTitle")} :</h2>
              {formData.tags.map((tag) => (
                <Tag
                  key={tag.name}
                  project={project}
                  tag={tag}
                  removable={Removable.RESSOURCE}
                  removableId={formData.uuid}
                />
              ))}
              <SelectList
                rootClassName={styles.tagInput}
                listClassName={styles.tagFormWrapper}
              >
                {({ select }) => ({
                  input: (
                    <div className={styles.iconBox}>
                      <AddTagIcon className={styles.icon} />
                    </div>
                  ),
                  list: (
                    <div className={styles.tagForm}>
                      <TagForm
                        filters={formData.tags}
                        type="resources"
                        submit={(tag) => {
                          !isRequestPending &&
                            assignTag({
                              assignable: Assignable.RESSOURCE,
                              assignableId: formData.uuid,
                              tag,
                              project,
                            });
                          select(tag);
                        }}
                      />
                    </div>
                  ),
                })}
              </SelectList>
            </div>
            <AllocationManagement resource={resource} />
            <div
              className={styles.affiliation}
              onClick={() =>
                editResource({
                  resource,
                  nextResource: {
                    ...formData,
                    affiliation:
                      formData.affiliation === "EXTERNAL"
                        ? "INTERNAL"
                        : "EXTERNAL",
                  },
                })
              }
            >
              <p>{t("editResource.affiliation")}</p>
              <Checkbox isChecked={formData.affiliation === "EXTERNAL"} />
            </div>

            <Calendar resource={resource} calendar={resource.weeklyCalendar} />
            <AssignCalendarHolidaysToResource
              planzone={planzone}
              resource={resource}
            />
            {isRequestSuccess && (
              <Projects resource={resource} redirect={cancel} />
            )}
            <button
              type="submit"
              className={styles.addButton}
              onClick={() => {
                pushView({
                  key: UseCases.addPlanzoneResourceToProjects,
                  data: resource,
                });
              }}
            >
              {t("editResource.addButton")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { ResourceEdition };
